import React, { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import Header from "../Components/Layout/Header";
import Footer from "../Components/Layout/Footer";
import Head from "../Components/Layout/Head";
import FooterNew from "../Components/Layout/FooterNew";

const PrivacyPolicyAI = () => {
  useEffect(() => {
    document.title = "Privacy Policy - ThinkQuotient.ai";
    AOS.init({
      duration: 900,
      once: true,
    });
  }, []);

  return (
    <>
      <div className="overflow-hidden bg-white">
        <Head />

        <section
          className="bg-gradient-to-r from-slate-100 via-white to-blue-50 py-12 md:py-16"
          data-aos="fade-up"
        >
          <div className="  md:px-8 lg:px-20">
            <div className="bg-white shadow-xl md:rounded-2xl p-6 md:p-12 border border-gray-100">
              <div className="text-center mb-10">
                <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
                  Privacy Policy
                </h2>

                <p className="text-lg text-gray-700 leading-relaxed max-w-5xl mx-auto">
                  ThinkQuotient.ai is committed to protecting the privacy,
                  confidentiality, and security of personal information,
                  business information, project data, and documents shared with
                  us while using our website, AI-enabled software development
                  services, consulting services, demos, prototypes, and custom
                  technology solutions.
                </p>
              </div>

              <div className="space-y-10">
                {/* 1. Scope */}
                <div>
                  <h2 className="text-2xl font-semibold text-blue-800 mb-4">
                    1. Scope of This Privacy Policy
                  </h2>
                  <p className="text-gray-600 leading-relaxed">
                    This Privacy Policy applies to information collected through
                    the ThinkQuotient.ai website, enquiry forms, demo forms,
                    consultation requests, email communication, project
                    discussions, AI proof-of-concept activities, custom software
                    development engagements, and related business interactions.
                  </p>
                </div>

                {/* 2. Information We Collect */}
                <div>
                  <h2 className="text-2xl font-semibold text-blue-800 mb-4">
                    2. Information We Collect
                  </h2>
                  <p className="text-gray-600 mb-4">
                    We may collect the following categories of information:
                  </p>

                  <ul className="list-decimal pl-6 text-gray-600 space-y-3">
                    <li>
                      <strong>Contact Information:</strong> Name, company name,
                      email address, phone number, designation, business
                      address, and communication details provided through forms,
                      calls, emails, or meetings.
                    </li>
                    <li>
                      <strong>Business Requirement Information:</strong> Details
                      related to your software requirements, AI use cases,
                      workflows, pain points, project scope, technical
                      expectations, budgets, timelines, and solution
                      preferences.
                    </li>
                    <li>
                      <strong>Demo and Consultation Data:</strong> Information
                      shared during product demos, discovery calls, AI
                      consultation sessions, webinars, or proof-of-concept
                      discussions.
                    </li>
                    <li>
                      <strong>Documents and Files:</strong> Files, sample
                      documents, reports, spreadsheets, PDFs, text data, images,
                      or other materials uploaded or shared with us for
                      analysis, demonstration, automation, RAG search,
                      summarization, extraction, or software development
                      purposes.
                    </li>
                    <li>
                      <strong>AI Interaction Data:</strong> Prompts, queries,
                      uploaded content, generated outputs, feedback, logs, and
                      related information processed through AI-enabled demos or
                      applications.
                    </li>
                    <li>
                      <strong>Website Usage Data:</strong> IP address, browser
                      type, device information, pages visited, referral source,
                      time spent on pages, and other technical information
                      collected through cookies, analytics tools, or server
                      logs.
                    </li>
                  </ul>
                </div>

                {/* 3. How We Use Information */}
                <div>
                  <h2 className="text-2xl font-semibold text-blue-800 mb-4">
                    3. How We Use Your Information
                  </h2>
                  <p className="text-gray-600 mb-4">
                    We use collected information for the following purposes:
                  </p>

                  <ul className="list-decimal pl-6 text-gray-600 space-y-3">
                    <li>
                      To respond to enquiries, consultation requests, and demo
                      requests.
                    </li>
                    <li>
                      To understand your business requirements and recommend
                      suitable AI or software solutions.
                    </li>
                    <li>
                      To prepare proposals, estimates, statements of work,
                      prototypes, demos, or proof-of-concept solutions.
                    </li>
                    <li>
                      To design, develop, test, deploy, support, and improve
                      custom AI-enabled software applications.
                    </li>
                    <li>
                      To perform document search, document summarization, data
                      extraction, analytics, workflow automation, chatbot, voice
                      bot, or RAG-based processing where requested by the
                      client.
                    </li>
                    <li>
                      To improve website performance, user experience, security,
                      and service quality.
                    </li>
                    <li>
                      To communicate service updates, business updates, event
                      invitations, webinars, or relevant solution information.
                    </li>
                    <li>
                      To comply with applicable legal, contractual, security,
                      and regulatory obligations.
                    </li>
                  </ul>
                </div>

                {/* 4. AI Data Processing */}
                <div>
                  <h2 className="text-2xl font-semibold text-blue-800 mb-4">
                    4. AI Data Processing and Use of Client Data
                  </h2>

                  <p className="text-gray-600 leading-relaxed mb-4">
                    ThinkQuotient.ai provides AI-enabled software development,
                    automation, analytics, document intelligence, RAG search,
                    chatbot, voice bot, and custom AI solution services. In such
                    engagements, client-provided data may be processed by AI
                    models, APIs, databases, vector stores, cloud
                    infrastructure, or internal software tools strictly for the
                    purpose of delivering the requested service.
                  </p>

                  <ul className="list-decimal pl-6 text-gray-600 space-y-3">
                    <li>
                      We do not sell client data, uploaded documents, business
                      data, or AI interaction data.
                    </li>
                    <li>
                      We do not use client confidential data to train public AI
                      models unless the client provides explicit written
                      permission.
                    </li>
                    <li>
                      Where third-party AI model providers or cloud platforms
                      are used, data may be processed through their
                      infrastructure only for the purpose of providing the
                      agreed service.
                    </li>
                    <li>
                      AI-generated outputs may require human review. We do not
                      guarantee that AI outputs will always be accurate,
                      complete, or suitable for final business, legal,
                      financial, medical, or compliance decisions without
                      appropriate review.
                    </li>
                    <li>
                      Clients are responsible for ensuring that they have the
                      right to share any documents, personal data, or business
                      data provided to us for AI processing or software
                      development.
                    </li>
                  </ul>
                </div>

                {/* 5. Consent */}
                <div>
                  <h2 className="text-2xl font-semibold text-blue-800 mb-4">
                    5. Consent
                  </h2>

                  <p className="text-gray-600 leading-relaxed">
                    By using our website, submitting an enquiry form, booking a
                    demo, communicating with us, or sharing documents or
                    business information, you consent to the collection and
                    processing of your information as described in this Privacy
                    Policy. You may withdraw your consent at any time by
                    contacting us. Withdrawal of consent may affect our ability
                    to provide certain services, demos, support, or ongoing
                    project activities.
                  </p>
                </div>

                {/* 6. Confidentiality */}
                <div>
                  <h2 className="text-2xl font-semibold text-blue-800 mb-4">
                    6. Confidentiality of Client Information
                  </h2>

                  <p className="text-gray-600 leading-relaxed mb-4">
                    We understand that clients may share sensitive business
                    information, workflows, internal documents, project data,
                    customer data, financial information, legal documents,
                    operational records, or other confidential materials with us
                    during consultation, development, or AI implementation.
                  </p>

                  <ul className="list-decimal pl-6 text-gray-600 space-y-3">
                    <li>
                      Access to client information is restricted to authorized
                      team members only.
                    </li>
                    <li>
                      Client information is used only for agreed business,
                      project, support, or service purposes.
                    </li>
                    <li>
                      Where required, confidentiality obligations may also be
                      governed by a separate NDA, service agreement, proposal,
                      or statement of work.
                    </li>
                    <li>
                      We take reasonable steps to prevent unauthorized access,
                      misuse, disclosure, alteration, or destruction of client
                      information.
                    </li>
                  </ul>
                </div>

                {/* 7. Cookies */}
                <div>
                  <h2 className="text-2xl font-semibold text-blue-800 mb-4">
                    7. Cookies and Tracking Technologies
                  </h2>

                  <p className="text-gray-600 leading-relaxed">
                    We may use cookies, analytics tools, tracking pixels, and
                    similar technologies to understand website usage, improve
                    performance, analyze traffic, personalize content, and
                    improve marketing effectiveness. You can disable cookies
                    through your browser settings; however, some website
                    features may not function properly.
                  </p>
                </div>

                {/* 8. Sharing of Information */}
                <div>
                  <h2 className="text-2xl font-semibold text-blue-800 mb-4">
                    8. Sharing of Information
                  </h2>

                  <p className="text-gray-600 mb-4">
                    We do not sell or rent your personal information. We may
                    share information only in the following cases:
                  </p>

                  <ul className="list-decimal pl-6 text-gray-600 space-y-3">
                    <li>
                      <strong>Service Providers:</strong> Cloud hosting
                      providers, AI model providers, email service providers,
                      analytics platforms, CRM tools, payment processors,
                      development tools, or other vendors who help us operate
                      our business and deliver services.
                    </li>
                    <li>
                      <strong>Project Delivery Partners:</strong> Authorized
                      consultants, developers, engineers, or implementation
                      partners involved in delivering the agreed service,
                      subject to confidentiality obligations.
                    </li>
                    <li>
                      <strong>Legal and Compliance Requirements:</strong>{" "}
                      Government authorities, regulators, courts, law
                      enforcement agencies, auditors, or legal advisors where
                      required by applicable law or to protect our rights.
                    </li>
                    <li>
                      <strong>Business Transfers:</strong> In case of merger,
                      restructuring, acquisition, or transfer of business
                      assets, subject to appropriate confidentiality and data
                      protection safeguards.
                    </li>
                  </ul>
                </div>

                {/* 9. Data Security */}
                <div>
                  <h2 className="text-2xl font-semibold text-blue-800 mb-4">
                    9. Data Security
                  </h2>

                  <p className="text-gray-600 leading-relaxed">
                    We implement reasonable technical, administrative, and
                    organizational safeguards to protect information from
                    unauthorized access, misuse, loss, alteration, or
                    disclosure. These measures may include access control,
                    encrypted communication, secure cloud infrastructure,
                    password protection, restricted user permissions, backups,
                    monitoring, and internal security practices. However, no
                    method of internet transmission or electronic storage is
                    completely secure, and we cannot guarantee absolute
                    security.
                  </p>
                </div>

                {/* 10. Data Retention */}
                <div>
                  <h2 className="text-2xl font-semibold text-blue-800 mb-4">
                    10. Data Retention
                  </h2>

                  <p className="text-gray-600 leading-relaxed">
                    We retain personal information, project data, enquiry data,
                    demo data, and client-shared information only for as long as
                    necessary to fulfill the purposes described in this Privacy
                    Policy, provide services, maintain business records, resolve
                    disputes, comply with legal obligations, or enforce
                    agreements. When information is no longer required, we may
                    delete, archive, anonymize, or securely dispose of it as per
                    applicable requirements and internal policies.
                  </p>
                </div>

                {/* 11. International Data Transfers */}
                <div>
                  <h2 className="text-2xl font-semibold text-blue-800 mb-4">
                    11. International Data Transfers
                  </h2>

                  <p className="text-gray-600 leading-relaxed">
                    Depending on the tools, cloud infrastructure, AI model
                    providers, analytics systems, or service providers used,
                    your information may be processed or stored in India or
                    other countries. Where cross-border processing is involved,
                    we take reasonable steps to ensure that appropriate
                    contractual, technical, and organizational safeguards are
                    followed.
                  </p>
                </div>

                {/* 12. Your Rights */}
                <div>
                  <h2 className="text-2xl font-semibold text-blue-800 mb-4">
                    12. Your Privacy Rights
                  </h2>

                  <p className="text-gray-600 mb-4">
                    Subject to applicable laws, you may have the following
                    rights regarding your personal information:
                  </p>

                  <ul className="list-decimal pl-6 text-gray-600 space-y-3">
                    <li>Request access to personal information held by us.</li>
                    <li>
                      Request correction or update of inaccurate or incomplete
                      information.
                    </li>
                    <li>
                      Request deletion or erasure of personal information where
                      applicable.
                    </li>
                    <li>
                      Withdraw consent for processing where consent is the basis
                      of processing.
                    </li>
                    <li>
                      Raise a grievance regarding privacy, security, or data
                      handling practices.
                    </li>
                  </ul>

                  <p className="text-gray-600 mt-4">
                    To exercise these rights, please contact us using the
                    details provided in the Contact Us section below.
                  </p>
                </div>

                {/* 13. Third Party Links */}
                <div>
                  <h2 className="text-2xl font-semibold text-blue-800 mb-4">
                    13. Third-Party Links
                  </h2>

                  <p className="text-gray-600 leading-relaxed">
                    Our website may contain links to third-party websites,
                    platforms, tools, videos, forms, or external resources. We
                    are not responsible for the privacy practices, content, or
                    security of such third-party websites. We encourage users to
                    review the privacy policies of external websites before
                    sharing any information.
                  </p>
                </div>

                {/* 14. Children */}
                <div>
                  <h2 className="text-2xl font-semibold text-blue-800 mb-4">
                    14. Children’s Privacy
                  </h2>

                  <p className="text-gray-600 leading-relaxed">
                    ThinkQuotient.ai is primarily intended for businesses,
                    professionals, institutions, and organizations seeking
                    AI-enabled software development and consulting services. Our
                    AI website and services are not directed toward children. We
                    do not knowingly collect personal information from children.
                    If you believe that a child has provided personal
                    information to us, please contact us so that we can take
                    appropriate action.
                  </p>
                </div>

                {/* 15. Updates */}
                <div>
                  <h2 className="text-2xl font-semibold text-blue-800 mb-4">
                    15. Updates to This Privacy Policy
                  </h2>

                  <p className="text-gray-600 leading-relaxed">
                    We may update this Privacy Policy from time to time to
                    reflect changes in our services, AI technologies, business
                    operations, legal requirements, security practices, or data
                    processing activities. The updated Privacy Policy will be
                    posted on this page with the revised effective date.
                  </p>
                </div>

                {/* Contact Section */}
                <div className="bg-gradient-to-r from-blue-50 via-white to-slate-50 p-6 md:p-8 rounded-xl shadow-md border border-blue-100">
                  <h2 className="text-2xl font-semibold text-blue-800 mb-4">
                    Contact Us
                  </h2>

                  <p className="text-gray-600 mb-5">
                    If you have any questions, concerns, requests, or grievances
                    regarding this Privacy Policy or our data handling
                    practices, you can contact us at:
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg p-4 border border-gray-100">
                      <p className="text-gray-700 md:whitespace-nowrap">
                        <strong>Call:</strong>{" "}
                        <a
                          href="tel:+917030055128"
                          className="text-blue-600 hover:underline"
                        >
                          +91-7030055128
                        </a>
                      </p>
                    </div>

                    <div className="bg-white rounded-lg p-4 border border-gray-100">
                      <p className="text-gray-700 md:whitespace-nowrap">
                        <strong>Email:</strong>{" "}
                        <a
                          href="mailto:enquiry@thinkquotient.ai"
                          className="text-blue-600 hover:underline"
                        >
                          enquiry@thinkquotient.ai
                        </a>
                      </p>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </section>

        <FooterNew />
      </div>
    </>
  );
};

export default PrivacyPolicyAI;
